import React, { useState } from 'react';
import { Button } from '@ipc/ipc-components';
import { PropTypes } from 'prop-types';
import { Link } from '@ipc/ipc-core';

import { StyledTextarea, TopButtonsWrapper } from '../../../styles';
import SecureComponent from '../SecureComponent';
import { Table, Download, FormLabel } from '../../../elements';
import { ROLE_ADMIN } from '../../../utils/access';

import {
  TABLE_COLUMNS, COLUMNS, FILE_NAME, TITLE,
} from './constants';
import { useRecordsList } from './hooks';
import AlertDialog from '../AlertDialog';
import UpdateApprovalStatusModal from '../UpdateApprovalStatusModal';

const RecordsListTable = ({ requestId, rerender, newWarning }) => {
  const [data, loading, bulkUpdateStatus, deleteRecords, updateItemComment, deleteRecordRequestComment] = useRecordsList(requestId, rerender);
  const [statusApprovalModal, updateStatusApprovalModal] = useState({});
  const [selected, updateSelected] = useState([]);
  const [comments, updateComments] = useState('');
  const [modal, updateModal] = useState({
    open: false,
    confirmTitle: 'Update',
    data: {},
  });

  const selectedStatusUpdate = status => () => {
    updateStatusApprovalModal({
      open: true,
      text: status,
      items: selected,
    });
  };

  const dismissUpdateApprovalStatusHandler = () => {
    updateStatusApprovalModal({ open: false });
  };

  const confirmUpdateStatusApprovalModal = () => () => {
    const { items, text: status } = statusApprovalModal;
    bulkUpdateStatus(items, status);
    updateStatusApprovalModal({ open: false });
  };

  const itemStatusUpdate = (item, status) => () => {
    updateStatusApprovalModal({
      open: true,
      text: status,
      items: [item.id],
    });
  };

  const updateSelectedHandler = (newSelected) => {
    updateSelected(newSelected);
  };

  const editRecordComment = record => () => {
    updateComments(record.comments);
    updateModal({
      ...modal,
      open: true,
      title: `${record.comments ? 'Edit' : 'Add'} record comment`,
      data: { record },
    });
  };

  const matchCommentPattern = str => /^[ -~\r\n]+$/.test(str);

  const modalConfirmHandler = () => {
    const { record } = modal.data;
    if (comments.length > 245) {
      newWarning('Comment length must be max 245 characters long', 10);
    } else if (!matchCommentPattern(comments)) {
      newWarning('Comment should only contain ASCII characters (English letters, numbers and special characters)', 10);
    } else {
      updateModal({
        ...modal,
        open: false,
      });
      updateItemComment(record, comments);
    }
  };

  const modalDismissHandler = () => {
    updateModal({
      ...modal,
      data: {},
      open: false,
    });
  };

  const commentChangeHandler = (event) => {
    updateComments(event.target.value);
  };

  const actions = item => (
    <>
      {item.groupStatus !== 'APPROVED'
        && <div onClick={itemStatusUpdate(item, 'APPROVED')}>Approve</div>
      }
      {item.groupStatus !== 'DISAPPROVED'
        && <div onClick={itemStatusUpdate(item, 'DISAPPROVED')}>Disapprove</div>
      }
      <Link to={`/receptacles/list?recordId=${item.id}`}>View Complete List</Link>
      <Link to={`/receptacles/list/pending?recordId=${item.id}`}>View Pending Approval</Link>
      <Link to={`/receptacles/list/approved?recordId=${item.id}`}>View Approved List</Link>
      <Link to={`/receptacles/list/disapproved?recordId=${item.id}`}>View Disapproved List</Link>
      <Link to={`/receptacles/list/failed?recordId=${item.id}`}>View Discrepancy List</Link>
      {!item.comments
        && <div onClick={editRecordComment(item)}>Add Comment</div>
      }
      {item.comments
        && <div onClick={editRecordComment(item)}>Edit Comment</div>
      }
      {item.comments
        && <div onClick={deleteRecordRequestComment(item.id)}>Delete Comment</div>
      }
      <SecureComponent roles={[ROLE_ADMIN]}>
        <div onClick={deleteRecords(item.id)}>Delete</div>
      </SecureComponent>
    </>
  );

  return (
    <>
      <AlertDialog
        title={modal.title}
        open={modal.open}
        confirmTitle={modal.confirmTitle}
        onConfirmation={modalConfirmHandler}
        onDismiss={modalDismissHandler}
      >
        <div className="FormRowInline">
          <FormLabel title="Comment" labelAbove>
            <StyledTextarea onChange={commentChangeHandler} value={comments || ''} />
          </FormLabel>
        </div>
      </AlertDialog>

      <UpdateApprovalStatusModal
        modal={statusApprovalModal}
        confirmHandler={confirmUpdateStatusApprovalModal}
        dismissHandler={dismissUpdateApprovalStatusHandler}
      />

      <TopButtonsWrapper>
        <Download columns={COLUMNS} fileName={FILE_NAME} title={TITLE} items={data} selected={selected} fontSize={6} />
        <Button onClick={selectedStatusUpdate('APPROVED')} disabled={!selected.length} data-testid="selected-approve">Approve</Button>
        <Button onClick={selectedStatusUpdate('DISAPPROVED')} disabled={!selected.length} data-testid="selected-disapprove">Disapprove</Button>
      </TopButtonsWrapper>
      <Table
        className="RecordList"
        columns={TABLE_COLUMNS}
        data={data}
        loading={loading}
        actions={actions}
        onSelect={updateSelectedHandler}
      />
    </>
  );
};

RecordsListTable.propTypes = {
  requestId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

export default RecordsListTable;
